import { createCrossWindowChannel } from '@/core/deep-dive/routing/cross-window-channel';
import { openPopup } from '@/shared/util/export';
import { convertWidgetToReactPath } from '@/core/widgets/export';

const HOST_WINDOW_ID_KEY = 'mni:host-window-id';

export function getHostWindowId(): string {
  let id = sessionStorage.getItem(HOST_WINDOW_ID_KEY);
  if (!id) {
    id = crypto.randomUUID();
    sessionStorage.setItem(HOST_WINDOW_ID_KEY, id);
  }
  return id;
}

type CloseGuard = () => boolean;
let closeGuard: CloseGuard | null = null;

export function registerCloseGuard(fn: CloseGuard): () => void {
  closeGuard = fn;
  return () => {
    if (closeGuard === fn) closeGuard = null;
  };
}

export function closeWindowSafely(win: Window = window): void {
  if (closeGuard && !closeGuard()) return;
  win.close();
}

const INLINE_SIDE_PANEL_ROUTES = ['/dashboard', '/tabs'];

export function isSidePanelInline(pathname: string = window.location.pathname): boolean {
  return !window.opener && INLINE_SIDE_PANEL_ROUTES.includes(pathname);
}

export const APP_WINDOW_WIDTH = 1200;

function windowFeatures(width: number) {
  const height = window.screen.availHeight;
  const left = Math.max(0, window.screen.availWidth - width);
  return `width=${width},height=${height},left=${left},top=0`;
}

const SIGNAL_CHANNEL = 'mni:deep-dive-window-signal';
type Signal =
  | { type: 'ping'; hostWindowId: string }
  | { type: 'ready'; hostWindowId: string; instanceId: string };
const signalChannel = createCrossWindowChannel<Signal>(SIGNAL_CHANNEL);

const READY_TIMEOUT_MS = 5000;
const HEALTH_CHECK_TIMEOUT_MS = 400;

function waitForInstance(
  hostWindowId: string,
  matches: (instanceId: string) => boolean,
  timeoutMs: number,
): Promise<string | null> {
  return new Promise(resolve => {
    let settled = false;
    const settle = (result: string | null) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      unsubscribe();
      resolve(result);
    };
    const unsubscribe = signalChannel.subscribe(signal => {
      if (
        signal.type === 'ready' &&
        signal.hostWindowId === hostWindowId &&
        matches(signal.instanceId)
      ) {
        settle(signal.instanceId);
      }
    });
    const timer = setTimeout(() => settle(null), timeoutMs);
  });
}

const OPEN_CHANNEL = 'mni:deep-dive-window-open';
interface DeepDiveWindowOpenMessage {
  appId: string;
  path?: string;
  targetInstanceId: string;
}
const openChannel = createCrossWindowChannel<DeepDiveWindowOpenMessage>(OPEN_CHANNEL);

export function subscribeDeepDiveWindowOpen(
  instanceId: string,
  handler: (appId: string, path?: string) => void,
): () => void {
  return openChannel.subscribe(message => {
    if (message.targetInstanceId !== instanceId) return;
    handler(message.appId, message.path);
  });
}

function appInstanceKey(hostWindowId: string) {
  return `mni:deep-dive-app-window-instance:${hostWindowId}`;
}

const appWindowRefs = new Map<string, Window>();

export async function openDeepDiveAppPopup(appId: string, path?: string): Promise<void> {
  const hostWindowId = getHostWindowId();
  const instanceKey = appInstanceKey(hostWindowId);
  const knownInstanceId = localStorage.getItem(instanceKey);
  const existingRef = appWindowRefs.get(hostWindowId);

  let result: { instanceId: string; isNew: boolean } | null = null;

  if (existingRef && !existingRef.closed && knownInstanceId) {
    existingRef.focus();
    result = { instanceId: knownInstanceId, isNew: false };
  } else if (knownInstanceId) {
    const alive = waitForInstance(
      hostWindowId,
      id => id === knownInstanceId,
      HEALTH_CHECK_TIMEOUT_MS,
    );
    signalChannel.publish({ type: 'ping', hostWindowId });
    if (await alive) result = { instanceId: knownInstanceId, isNew: false };
  }

  if (!result) {
    const instanceId = crypto.randomUUID();
    const ready = waitForInstance(hostWindowId, id => id === instanceId, READY_TIMEOUT_MS);
    const ref = window.open(
      `/deep-dive-window?instance=${instanceId}&host=${hostWindowId}&app=${encodeURIComponent(appId)}${
        path ? `&path=${encodeURIComponent(path)}` : ''
      }`,
      `mni-deep-dive-app-${hostWindowId}`,
      windowFeatures(APP_WINDOW_WIDTH),
    );
    if (!ref) {
      console.warn('openDeepDiveAppPopup: popup blocked.');
      return;
    }
    appWindowRefs.set(hostWindowId, ref);
    const readyInstanceId = await ready;
    if (!readyInstanceId) {
      console.warn('openDeepDiveAppPopup: could not reach the deep-dive window (unresponsive).');
      return;
    }
    result = { instanceId: readyInstanceId, isNew: true };
  }

  if (!result.isNew) {
    openChannel.publish({ appId, path, targetInstanceId: result.instanceId });
  }
}

export function announceDeepDiveWindowInstance(
  hostWindowId: string,
  instanceId: string,
): () => void {
  const key = appInstanceKey(hostWindowId);
  localStorage.setItem(key, instanceId);
  signalChannel.publish({ type: 'ready', hostWindowId, instanceId });

  const unsubscribe = signalChannel.subscribe(signal => {
    if (signal.type === 'ping' && signal.hostWindowId === hostWindowId) {
      signalChannel.publish({ type: 'ready', hostWindowId, instanceId });
    }
  });

  const release = () => {
    if (localStorage.getItem(key) === instanceId) localStorage.removeItem(key);
  };
  window.addEventListener('beforeunload', release);

  return () => {
    unsubscribe();
    release();
    window.removeEventListener('beforeunload', release);
  };
}

type DeepDiveOpenHandler = (appId: string, path?: string) => void;
let inlineDeepDiveHandler: DeepDiveOpenHandler | null = null;

export function registerInlineDeepDiveHandler(handler: DeepDiveOpenHandler): () => void {
  inlineDeepDiveHandler = handler;
  return () => {
    if (inlineDeepDiveHandler === handler) inlineDeepDiveHandler = null;
  };
}

// Opens a deep-dive app wherever is appropriate for the current context —
// inline in the side panel, or in a separate popup window — without the
// caller needing to know which.
export async function openDeepDiveApp(appId: string, path?: string): Promise<void> {
  if (isSidePanelInline()) {
    inlineDeepDiveHandler?.(appId, path);
    return;
  }

  await openDeepDiveAppPopup(appId, path);
}

type DashboardWidgetOpenHandler = (widgetId: string, path?: string) => void;
let inlineDashboardWidgetHandler: DashboardWidgetOpenHandler | null = null;

export function registerInlineDashboardWidgetHandler(
  handler: DashboardWidgetOpenHandler,
): () => void {
  inlineDashboardWidgetHandler = handler;
  return () => {
    if (inlineDashboardWidgetHandler === handler) inlineDashboardWidgetHandler = null;
  };
}

// Reverse direction: from inside a deep-dive app, open/focus a dashboard
// widget — inline in the dashboard, or by opening a popup for it and closing
// the current deep-dive window.
export function openDashboardWidgetFromDeepDive(widgetId: string, path?: string): void {
  if (isSidePanelInline()) {
    if (inlineDashboardWidgetHandler) {
      inlineDashboardWidgetHandler(widgetId, path);
    } else {
      console.warn('openDashboardWidgetFromDeepDive: no dashboard registered to receive this yet.');
    }
    return;
  }

  // Named by widgetId, same as useWidgetActions.popout() — so this targets
  // the exact same window that button would, regardless of any incidental
  // difference in how the two build the url.
  openPopup(convertWidgetToReactPath(widgetId, path), 600, 800, widgetId);
  closeWindowSafely();
}
